import Axios from 'axios';
import {jwtDecode} from "jwt-decode";

import {getAccessToken, getApiKey, getLocale, removeAllData, setApiKey} from "@/Foundation/helpers/storage";

const defaultHeaders = {
  Accept: 'application/x-www-form-urlencoded, application/zip, application/xml, plain/text',
};

interface PostOptions {
  security?: string | null;
  params?: {};
  data?: {};
  headers?: {};
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream' | undefined | null;
}

interface GetOptions {
  security?: string | null;
  params?: {};
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream' | undefined | null;
}

interface DeleteOptions {
  security?: string | null;
  params?: {};
}

interface ImportMetaEnv {
  readonly VITE_SITE_URL: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

/**
 * Manage the Server requests.
 */
export default {
  beforeRequest() {
  },

  afterRequest() {
  },

  onError(e) {
  },

  async post(path, options = {security: null, params: {}, data: {}, headers: {}, responseType: null} as PostOptions) {
    const authorization = await this.resolveAuthorization(options.security).catch(e => this.onError(e));

    if (!authorization) {
      return {status: 0, data: {}, headers: {}};
    }

    const axiosOptions = {
      baseURL: import.meta.env.VITE_SITE_URL,
      params: {...options.params},
      headers: {...authorization, ...options.headers, ...defaultHeaders, 'Accept-Language': getLocale()},
      responseType: options.responseType ?? 'text',
      validateStatus: status => status >= 200 && status < 500,
    };

    let formData;
    if (options.headers?.['Content-Type'] === 'multipart/form-data') {
      formData = new FormData();
      Object.entries(options.data).forEach(([key, value]) => {
        formData.append(key, value);
      });
    } else {
      formData = this.http_build_query(options.data);
    }

    this.beforeRequest();
    const {data, status, headers} = await Axios.post(path, formData, axiosOptions).catch(e => {
      console.warn('error', e);
      return {data: null, status: 0, headers: {}};
    });
    this.afterRequest();

    if (status === 403) {
      this.onError(new Error(data));
      return {status: 0, data: {}, headers: {}};
    }

    let parsedData;
    if (headers['content-type']?.startsWith('application/x-www-form-urlencoded')) {
      if (options.responseType === 'blob') {
        parsedData = this.parse_str(await data.text());
      } else {
        parsedData = this.parse_str(data);
      }
    } else {
      parsedData = data;
    }

    return {status, data: parsedData, headers};
  },

  async get(path, options = {security: null, params: {}, responseType: null} as GetOptions) {
    const authorization = await this.resolveAuthorization(options.security).catch(e => this.onError(e));

    if (!authorization) {
      return {status: 0, data: {}, headers: {}};
    }

    const axiosOptions = {
      baseURL: import.meta.env.VITE_SITE_URL,
      params: {...options.params},
      headers: {...authorization, ...defaultHeaders, 'Accept-Language': getLocale()},
      responseType: options.responseType ?? 'text',
      validateStatus: status => status >= 200 && status < 500,
    };

    this.beforeRequest();
    const {data, status, headers} = await Axios.get(path, axiosOptions).catch(e => {
      console.warn('error', e);
      return {data: null, status: 0, headers: {}};
    });
    this.afterRequest();

    if (status === 403) {
      this.onError(new Error(data));
      return {status: 0, data: {}, headers: {}};
    }

    let parsedData;
    if (headers['content-type']?.startsWith('application/x-www-form-urlencoded')) {
      if (options.responseType === 'blob') {
        parsedData = this.parse_str(await data.text());
      } else {
        parsedData = this.parse_str(data);
      }
    } else {
      parsedData = data;
    }

    return {status, data: parsedData, headers};
  },

  async delete(path, options = {security: null, params: {}} as DeleteOptions) {
    const authorization = await this.resolveAuthorization(options.security).catch(e => this.onError(e));

    if (!authorization) {
      return {status: 0, data: {}, headers: {}};
    }

    const axiosOptions = {
      baseURL: import.meta.env.VITE_SITE_URL,
      params: {...options.params},
      headers: {...authorization, ...defaultHeaders, 'Accept-Language': getLocale()},
      validateStatus: status => status >= 200 && status < 500,
    };

    this.beforeRequest();
    const {data, status, headers} = await Axios.delete(path, axiosOptions).catch(e => {
      console.warn('error', e);
      return {data: null, status: 0, headers: {}};
    });
    this.afterRequest();

    if (status === 403) {
      this.onError(new Error(data));
      return {status: 0, data: {}, headers: {}};
    }

    return {status, data: this.parse_str(data), headers};
  },

  async resolveAuthorization(security: string | null = null) {
    if (security === 'access_token') {
      const accessToken = getAccessToken();

      if (!accessToken) {
        window.location.href = '/sign-in';
        return null;
      }

      this.preprocessAccessToken(accessToken);

      return {
        Authorization: `Bearer ${accessToken}`,
      };
    }

    if (security === 'api_key') {
      let result;
      const apiKey = getApiKey();

      if (apiKey) {
        result = {status: 200, data: {key: apiKey}};
      } else {
        result = await this.get('/api/settings/main-api-key', {security: 'access_token'});
        if (result.data?.key) {
          setApiKey(result.data.key);
        }
      }

      if (result.status === 204) {
        throw new Error('t:error.api_key_is_not_generated');
      }

      return {
        Authorization: `Key ${result.data.key}`,
      };
    }

    return {};
  },

  parse_str(query) {
    if (!query || query === '') return null;

    query = query.includes('?') ? query.substring(query.indexOf('?') + 1) : query;

    const re = /([^&=]+)=?([^&]*)/g;
    const decodeRE = /\+/g;

    const decode = (str) => decodeURIComponent(str.replace(decodeRE, ' '));

    const params = {};
    let e;

    while ((e = re.exec(query)) !== null) {
      let k = decode(e[1]);
      let v = decode(e[2]);

      if (k.endsWith('[]')) {
        k = k.slice(0, -2);
        (params[k] ||= []).push(v);
      } else {
        params[k] = v;
      }
    }

    const assign = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      for (let i = 0; i < lastKeyIndex; ++i) {
        const key = keyPath[i];
        if (!(key in obj)) obj[key] = {};
        obj = obj[key];
      }
      obj[keyPath[lastKeyIndex]] = value;
    };

    for (const prop in params) {
      const structure = prop.split('[');
      if (structure.length > 1) {
        const levels = structure.map(item => item.replace(/[?[\]\\ ]/g, ''));
        assign(params, levels, params[prop]);
        delete params[prop];
      }
    }

    function convertNumericKeys(obj) {
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        const keys = Object.keys(obj);
        if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
          const arr = [];
          keys.forEach(k => {
            arr[Number(k)] = convertNumericKeys(obj[k]);
          });
          return arr;
        } else {
          for (const key in obj) {
            obj[key] = convertNumericKeys(obj[key]);
          }
          return obj;
        }
      }
      return obj;
    }

    return convertNumericKeys(params);
  },

  http_build_query(obj) {
    if (typeof obj !== 'object' || obj === null) return null;

    const getPairs = (obj, keys = []) =>
      Object.entries(obj).reduce((pairs, [key, value]) => {
        if (typeof value === 'object' && value !== null) {
          pairs.push(...getPairs(value, [...keys, key]));
        } else {
          pairs.push([[...keys, key], value]);
        }
        return pairs;
      }, []);

    return getPairs(obj)
      .map(([[key0, ...keysRest], value]) => `${key0}${keysRest.map(a => `[${a}]`).join('')}=${encodeURIComponent(value)}`)
      .join('&');
  },

  preprocessAccessToken(accessToken) {
    const user = jwtDecode(accessToken);
    const now = Math.floor(Date.now() / 1000);
    if (now >= user.exp) {
      removeAllData();
      window.location.href = `/sign-in`;
    }
  },
};
